/** "Labour readiness" panel for the Labour Management demo screen - surfaces
 * training-related labour risk (missing allergen/food-safety training, expiring
 * certificates, role readiness) without duplicating the full Training module elsewhere
 * in Workforce Management. */

import { Box, Grid, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReadinessIssue, ReadinessMetric } from '../types';
import { StatusChip } from './StatusChip';

interface ReadinessPanelProps {
  metrics: ReadinessMetric[];
  issues: ReadinessIssue[];
}

const ReadinessPanel = ({ metrics, issues }: ReadinessPanelProps) => {
  const { t } = useTranslation('common');

  return (
    <Stack spacing={4}>
      <Grid container
        spacing={3}>
        {metrics.map((metric) => (
          <Grid item
            xs={12}
            sm={6}
            md={2.4}
            key={metric.key}>
            <Typography variant="textXS"
              color="text.secondary">{metric.label}</Typography>
            <Typography variant="textLG"
              fontWeight={600}>{metric.value}</Typography>
          </Grid>
        ))}
      </Grid>

      <Box>
        <Typography variant="textSM"
          fontWeight={600}
          sx={{ mb: 2 }}>
          {t('labourManagementReadinessIssues', 'Role readiness issues')}
        </Typography>
        <Stack spacing={2}>
          {issues.length === 0 && (
            <Typography variant="textSM"
              color="text.secondary">
              {t('labourManagementNoReadinessIssues', 'No readiness issues for the current filters.')}
            </Typography>
          )}
          {issues.map((issue) => (
            <Stack key={issue.id}
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              spacing={2}
              sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box>
                <Typography variant="textSM"
                  fontWeight={600}>{issue.site}</Typography>
                <Typography variant="textSM"
                  color="text.secondary">{issue.description}</Typography>
              </Box>
              <StatusChip status={issue.severity}
                label={t(`labourManagementSeverity${issue.severity === 'critical' ? 'Critical' : 'Warning'}`, issue.severity === 'critical' ? 'Critical' : 'Warning')} />
            </Stack>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default ReadinessPanel;
